import React from 'react';
import {
  groupingBasisOf,
  formatGroupHeading,
  oldestGroupPlacedByAge,
} from 'utils/groupLabel';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import styles from './ClubDashboardComponent.module.css';
import EventCard from '../EventCard';
import SeasonPointsBadge from '../SeasonPointsBadge';
import routes from 'routes';

// The group a club record is for: "U11" for an Under club, "11" for an Age
// club and "Y6" for a club that groups by year level, while the club places
// that group by age; an adult's number, and Senior or Masters, as they are.
const formatAgeGroup = (ageGroup, club) => {
  const age = parseInt(ageGroup);
  return !isNaN(age) && age <= oldestGroupPlacedByAge(club)
    ? formatGroupHeading(groupingBasisOf(club), ageGroup)
    : ageGroup;
};

const ClubDashboardComponent = ({
  selectedClub,
  clubRecords,
  recentMeets,
  upcomingMeets,
}) => {
  return (
    <div className={styles.container}>
      {/* Recent Records (Doug, 2026-10-26: one title for clubs and schools) */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Recent Records</h2>
          <a href="club-records" className={styles.seeAllLink}>
            See All
          </a>
        </div>
        <div className={styles.recordsGrid}>
          {clubRecords.map((record, index) => (
            <div key={index} className={styles.recordCard}>
              <div className={styles.recordCategory}>
                <span>
                  <span className={styles.eventType}>{record.eventType}</span>{' '}
                  <span className={styles.separator}>•</span>{' '}
                  {formatAgeGroup(record.ageGroup, selectedClub)}{' '}
                  <span className={styles.separator}>•</span> {record.gender}
                </span>
                <span className={styles.recordResult}>
                  {record.formattedValue}
                </span>
              </div>
              <div className={styles.recordHolder}>
                {record.athleteName} • {record.achievedAt}
                {record.isNew && (
                  <span className={styles.recentBadge}>NEW</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Recent Meets */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Completed Meets</h2>
          <a href="meets?status=Complete" className={styles.seeAllLink}>
            See All
          </a>
        </div>

        <div className={styles.meetsList}>
          {recentMeets.map((meet, index) => (
            <div key={index} className={styles.meetItem}>
              <div>
                <div className={styles.meetDateRow}>
                  <div className={styles.meetDate}>
                    {meet.date} • {meet.time}
                  </div>
                  <SeasonPointsBadge
                    countsForSeasonPoints={meet.countsForSeasonPoints}                  />
                </div>
                <div className={styles.meetName}>{meet.meetName}</div>
                <div className={styles.meetLocation}>{meet.location}</div>
              </div>
              <div className={styles.meetButtons}>
                <Link
                  to={routes.admin.showMeet.url(
                    selectedClub?.clubID,
                    meet.meetID
                  )}
                  className={styles.meetDetailsButton}
                >
                  View Details
                </Link>
                <Link to={meet.viewUrl} className={styles.meetStatus}>
                  View Results
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Upcoming Meets */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Upcoming Meets</h2>
          <a href="meets?status=Upcoming" className={styles.seeAllLink}>
            See All
          </a>
        </div>

        <div className={styles.upcomingCards}>
          {upcomingMeets.map((event, index) => (
            <EventCard key={index} meet={event} clubId={selectedClub?.clubID} />
          ))}
        </div>
      </div>
    </div>
  );
};

ClubDashboardComponent.propTypes = {
  selectedClub: PropTypes.object,
  clubRecords: PropTypes.array,
  recentMeets: PropTypes.array,
  upcomingMeets: PropTypes.array,
};

export default ClubDashboardComponent;
